import { Disclosure } from "@headlessui/react";
import React from "react";
import { Link } from "react-router-dom";
import { useClientPersonalInformationContext } from "../../api/clientPersonalInformation";
import { useOrganizationContext } from "../../api/current-organization/organizationContext";
import { useTalentPersonalInformationContext } from "../../api/talentPersonalInformation";
import useCurrentUser from "../../auth/useCurrentUser";
import { useIsExtraLargeScreen, useIsSmallScreen } from "../../hooks/screenSizes";
import { useAppPaths } from "../../Routes";
import { buildTalentNavItems, buildClientNavItems } from "./SideMenuNavItems";
import SideNavMenuItem from "./SideNavMenuItem";
import { SignOutButton } from "./SignOutButton";
import { useUserGetMe } from "../../api/userGetMe";
import DialogBox, { useDialogBoxState } from "../DialogBox";
import { dialogWidths } from "../DialogBox/DialogBox";
import Button from "../Button";
import { hasClaim } from "../../auth/hasClaim";
import { UserRole } from "../../models/app/userMe";
import NylasConnectIndicator from "../Nylas/NylasConnectIndicator";

export type SideNavMenuProps = {
    close: () => void
}

const SideNavMenu = ({
    close,
}: SideNavMenuProps) => {
    const currentUser = useCurrentUser();
    const { claims } = currentUser;
    const { giggedClientIsVerified } = useUserGetMe();
    const appPaths = useAppPaths();
    const { talentPersonalInformation } = useTalentPersonalInformationContext();
    const { clientPersonalInformation } = useClientPersonalInformationContext();
    const isSmallScreen = useIsSmallScreen();
    const isExtraLarge = useIsExtraLargeScreen();
    const { organizationConfig, gigTerminology, talentTerminologyPlural } = useOrganizationContext();
    const accountVerificationDialog = useDialogBoxState();
    const isOtm = organizationConfig.name === "Gigged.AI";
    const isBadgesEnabled = organizationConfig.isBadgesEnabled;
    const isDefaultClient = hasClaim(UserRole.Default, claims);
    const isTalent = hasClaim(UserRole.AC, claims);

    const showFindTalentMenuItem =
        !organizationConfig?.isTalentProfilesPrivate ||
        isDefaultClient;

    const navItems = isDefaultClient
        ? buildClientNavItems(currentUser.clientId)
        : buildTalentNavItems(currentUser.talentId);

    const wrapInContainer = (children: React.ReactNode) => (
        isExtraLarge ? (
            <div className="fixed xl:static inset-y-0 left-0 w-[288px] shrink-0 p-5 pr-12 bg-app-background z-40">
                {children}
            </div>
        ) : (
            <Disclosure.Panel className="fixed inset-y-0 left-0 w-[288px] p-5 pr-12 bg-white z-[1010] overflow-y-auto">
                {children}
            </Disclosure.Panel>
        )
    );

    const profileImage = () => {
        if (!clientPersonalInformation?.profileImageUrl && !talentPersonalInformation?.profileImageUrl) {
            return (
                <div className="h-32 w-32 pt-[4px] text-[54px] flex items-center justify-center rounded-full bg-surface mx-auto mb-8">
                    {currentUser.userGivenName?.length > 0 && currentUser.userGivenName[0].toUpperCase()}
                </div>
            );
        }
        return (
            <img
                src={isTalent ? talentPersonalInformation?.profileImageUrl : clientPersonalInformation?.profileImageUrl}
                className="h-32 w-32 object-cover rounded-full mx-auto mb-8"
            />
        );
    };

    return wrapInContainer(
        <>
            <nav>
                {profileImage()}
                <div className="text-center mb-8 break-word">
                    {isDefaultClient ?
                        <>
                            <p className="text-title-medium font-bold">
                                {clientPersonalInformation?.companyName}
                            </p>
                            <span>User: {currentUser.userGivenName} {currentUser.userFamilyName}</span>
                        </>
                        :
                        <p className="text-title-medium font-bold break-words">
                            {currentUser.userGivenName}
                        </p>
                    }

                    <div className="flex justify-center mt-1">
                        <NylasConnectIndicator variant="status" />
                    </div>
                </div>

                {isDefaultClient && (
                    <Disclosure.Button
                        className="block w-full mb-8 text-primary-bg-text font-bold py-[0.75rem] px-[1.5rem] rounded-full bg-primary hover:bg-primary-hover focus:outline-3 text-center transition-colors ease-in-out duration-200"
                        as={Link}
                        to={!isOtm || !isBadgesEnabled || giggedClientIsVerified ? appPaths.gigs.createIntro : "#"}
                        onClick={isOtm && isBadgesEnabled && !giggedClientIsVerified ? accountVerificationDialog.open : undefined}
                    >
                        Post a {gigTerminology.toLowerCase()}
                    </Disclosure.Button>
                )}

                <div className="space-y-3">
                    {!isSmallScreen && (
                        <div className="border-y border-surface ">
                            {showFindTalentMenuItem && isDefaultClient &&
                                <Disclosure.Button
                                    as={Link}
                                    className="block text-primary-text font-bold text-left px-3 py-3 w-full border-b border-surface"
                                    to={appPaths.talents.findTalent}
                                >
                                    Find {talentTerminologyPlural}
                                </Disclosure.Button>
                            }
                            {/* <Disclosure.Button
                                as={Link}
                                className="block text-primary-text font-bold text-left px-3 py-3 w-full border-b border-surface"
                                to={appPaths.gigs.findAGig}
                            >
                                Find a {gigTerminology}
                            </Disclosure.Button> */}
                            {isOtm && (
                                <a href="https://gigged.ai" target="_blank">
                                    <Disclosure.Button

                                        className="block text-primary-text font-bold text-left px-3 py-3 w-full"

                                    >
                                        Back to Home Page
                                    </Disclosure.Button>
                                </a>
                            )}
                        </div>
                    )}

                    {navItems.map((navItem) => (
                        <SideNavMenuItem
                            key={navItem.name}
                            item={navItem}
                            onLinkClick={() => !isExtraLarge && close()}
                        />
                    ))}

                    <SignOutButton />
                </div>
            </nav>
            <DialogBox
                {...accountVerificationDialog}
                title="Account not verified"
                maxWidth={dialogWidths.medium}
            >
                <div className="flex flex-col gap-4">
                    <p>To help us maintain the quality and security of our platform, all new accounts go through a verification process.</p>
                    <p>We aim to complete this process within 24 hours, but occasionally it can take a little longer.</p>
                    <p>If you have an urgent {gigTerminology.toLowerCase()} requirement and your account is still unverified, please contact our support team and we'll be happy to help.</p>
                    <Button type="button" variant="primary" className="mt-2"><a href="mailto:customersuccess@gigged.ai">Contact support</a></Button>
                </div>
            </DialogBox>
        </>
    );
};

export default SideNavMenu;
